"use client";

import { useEffect, useState } from "react";
import { useAuth as useClerkAuth } from "@clerk/nextjs";
import { useAuthStore } from "@/store/auth-store";
import type { UserRole, AuthDTO } from "@/types/api/auth";

/**
 * Decode JWT token and extract payload
 */
function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Decode the payload (second part) - handle both Node.js and browser environments
    const payload = parts[1];
    const decoded = JSON.parse(
      typeof Buffer !== "undefined"
        ? Buffer.from(payload, "base64").toString("utf-8")
        : atob(payload)
    );
    return decoded;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
}

/**
 * Hook to sync Clerk authentication with your auth-store
 * Returns loading state so UI can show loading indicator while token syncs
 */
export function useClerkSync() {
  const { userId, getToken, isLoaded, isSignedIn } = useClerkAuth();
  const setAuthState = useAuthStore((state) => state.setAuthState);
  const logout = useAuthStore((state) => state.logout);
  const [isTokenReady, setIsTokenReady] = useState(false);

  const resolveRoleFromDatabase = async (token: string): Promise<UserRole | null> => {
    try {
      const allowedRoles: UserRole[] = ["admin", "staff", "student", "preceptor", "super_admin"];

      // Prefer same-origin Next.js route to avoid mixed-content/CORS when the app runs on https://localhost:3000.
      // This route uses the Clerk session cookies server-side to fetch the backend user.
      let data: any = null;
      try {
        const sameOriginResponse = await fetch(`/api/auth/user`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          cache: "no-store",
        });

        if (sameOriginResponse.ok) {
          data = await sameOriginResponse.json();
        }
      } catch {
        // ignore and fall back
      }

      // Fallback: call backend directly (may be blocked in browser if app is https and API is http)
      if (!data) {
        const API_BASE =
          process.env.NEXT_PUBLIC_API_URL ||
          process.env.NEXT_PUBLIC_API_BASE_URL ||
          "http://localhost:8000";
        const normalized = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;
        const url = normalized.endsWith("/api")
          ? `${normalized}/auth/user`
          : `${normalized}/api/auth/user`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-store",
        });

        if (!response.ok) return null;
        data = await response.json();
      }

      // Clerk-authenticated API returns role as a string at data.role
      const apiRole: string | undefined =
        data?.data?.role ?? data?.role ?? data?.data?.user?.role ?? data?.user?.role;
      if (typeof apiRole === "string") {
        const normalized = apiRole.trim().toLowerCase();
        return allowedRoles.includes(normalized as UserRole) ? (normalized as UserRole) : null;
      }

      const roleId: number | undefined =
        data?.data?.user?.role_id ?? data?.data?.role_id ?? data?.user?.role_id ?? undefined;
      const userType: string | undefined =
        data?.data?.user?.user_type ?? data?.data?.user_type ?? data?.user?.user_type ?? undefined;

      if (typeof userType === "string") {
        const normalized = userType.toLowerCase();
        return allowedRoles.includes(normalized as UserRole) ? (normalized as UserRole) : null;
      }

      if (typeof roleId === "number") {
        const roleMap: Record<number, UserRole> = {
          1: "admin",
          2: "student",
          3: "staff",
          4: "preceptor",
        };
        return roleMap[roleId] ?? null;
      }

      return null;
    } catch (error) {
      console.error("Failed to resolve role from database:", error);
      return null;
    }
  };

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn || !userId) {
      // User is not signed in, clear auth state and mark as ready
      logout();
      setIsTokenReady(true);
      return;
    }

    // User is signed in, get the token and sync to auth-store
    const syncClerkAuth = async () => {
      try {
  // Use default Clerk JWT template; avoids failures if a custom template isn't configured.
  // If you need custom claims, set an explicit template in Clerk Dashboard and pass its name here.
  let token = await getToken();

        // Retry once if token is null (sometimes happens right after signup)
        if (!token) {
          console.warn("Token not ready, retrying in 500ms...");
          await new Promise(resolve => setTimeout(resolve, 500));
          token = await getToken();
        }

        if (!token) {
          console.warn("No Clerk token retrieved after retry. User may not have a complete session yet.");
          setIsTokenReady(true);
          return;
        }

        // Decode the JWT token to extract user information
        const decoded = decodeToken(token);
        if (!decoded) {
          console.warn("Failed to decode Clerk token");
          setIsTokenReady(true);
          return;
        }

        console.log("Decoded token:", decoded);

        const resolveBoolean = (value: unknown): boolean | undefined => {
          if (value === undefined || value === null) return undefined;
          if (typeof value === "boolean") return value;
          if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            if (normalized === "true") return true;
            if (normalized === "false") return false;
          }
          if (typeof value === "number") return value !== 0;
          return undefined;
        };

        const allowedRoles: UserRole[] = ["admin", "staff", "student", "preceptor", "super_admin"];
        const roleClaimRaw = (decoded?.role as string) || (decoded?.user_role as string) || null;
        const roleClaim = typeof roleClaimRaw === "string" ? roleClaimRaw.toLowerCase() : null;

        let resolvedRole: UserRole = "student";
        if (roleClaim && allowedRoles.includes(roleClaim as UserRole)) {
          resolvedRole = roleClaim as UserRole;
        } else {
          const dbRole = await resolveRoleFromDatabase(token);
          if (dbRole) {
            resolvedRole = dbRole;
          }
        }
        const isVerified = resolveBoolean(decoded?.isVerified);
        const onboarded = resolveBoolean(decoded?.onboarded ?? decoded?.onboarding);

        const authData: AuthDTO = {
          user: {
            id: typeof decoded?.sub === "string" ? decoded.sub : (decoded?.user_id as string) ?? "",
            email:
              (decoded?.user_email as string) ||
              (decoded?.email as string) ||
              ((decoded?.email_addresses as Array<{ email_address: string }> | undefined)?.[0]?.email_address as string) ||
              "",
            name:
              decoded?.user_fname && decoded?.user_lname
                ? `${decoded.user_fname} ${decoded.user_lname}`
                : ((decoded?.name as string) || ""),
            role: resolvedRole,
            track: (decoded?.type as string) || undefined,
            status: isVerified ? "verified" : onboarded ? "pending" : undefined,
          },
          token,
          role: resolvedRole,
        };

        console.log("Setting auth state:", authData);

        // Save to auth-store
        setAuthState(authData);
        setIsTokenReady(true);
      } catch (error) {
        console.error("Failed to sync Clerk auth:", error);
        setIsTokenReady(true);
      }
    };

    syncClerkAuth();
  }, [isLoaded, isSignedIn, userId, getToken, setAuthState, logout]);

  return { isTokenReady };
}
